/**
 * Line-chart geometry shared by the weight and progress charts: pure, no DOM.
 * Points are spread evenly by index; the value axis gets 8% padding; a single or
 * flat series gets a ±1 span so nothing divides by zero.
 */
export interface ChartBox {
  w: number;
  h: number;
  padX: number;
  padY: number;
}

export const CHART_BOX: ChartBox = { w: 320, h: 160, padX: 12, padY: 16 };

export type Plotted<P> = P & { x: number; y: number };

export interface ChartLayout<P> {
  w: number;
  h: number;
  padX: number;
  padY: number;
  coords: Plotted<P>[];
  line: string;
  area: string;
  /** Padded axis bounds (what the weight chart prints). */
  min: number;
  max: number;
  /** Smallest and largest actual value. */
  lo: number;
  hi: number;
}

/** Lay `points` out; points whose value is not finite are ignored; null when none remain. */
export function layoutSeries<P>(
  points: readonly P[],
  value: (p: P) => number,
  box: Partial<ChartBox> = {},
): ChartLayout<P> | null {
  const w = box.w ?? CHART_BOX.w;
  const h = box.h ?? CHART_BOX.h;
  const padX = box.padX ?? CHART_BOX.padX;
  const padY = box.padY ?? CHART_BOX.padY;
  const pts = points.filter((p) => Number.isFinite(value(p)));
  if (pts.length === 0) return null;

  const values = pts.map(value);
  const lo = Math.min(...values);
  const hi = Math.max(...values);
  let min = lo;
  let max = hi;
  if (min === max) {
    min -= 1;
    max += 1;
  }
  const span = max - min;
  min -= span * 0.08;
  max += span * 0.08;

  const innerW = w - padX * 2;
  const innerH = h - padY * 2;
  const coords = pts.map((p, i) => ({
    ...p,
    x: pts.length === 1 ? padX + innerW / 2 : padX + (i / (pts.length - 1)) * innerW,
    y: padY + ((max - value(p)) / (max - min)) * innerH,
  }));

  const line = coords
    .map((c, i) => `${i === 0 ? "M" : "L"} ${c.x.toFixed(1)} ${c.y.toFixed(1)}`)
    .join(" ");
  const last = coords[coords.length - 1];
  const area = `${line} L ${last.x.toFixed(1)} ${(h - padY).toFixed(1)} L ${coords[0].x.toFixed(1)} ${(h - padY).toFixed(1)} Z`;

  return { w, h, padX, padY, coords, line, area, min, max, lo, hi };
}

/** Index of the plotted point closest (by x) to a viewBox x; -1 for no points. */
export function nearestIndex(coords: readonly { x: number }[], x: number): number {
  let best = -1;
  let bestDist = Infinity;
  for (let i = 0; i < coords.length; i++) {
    const d = Math.abs(coords[i].x - x);
    if (d < bestDist) {
      best = i;
      bestDist = d;
    }
  }
  return best;
}

/** Screen x of a pointer event -> viewBox x (the svg is scaled to its box width). */
export function toViewX(clientX: number, rect: { left: number; width: number }, viewWidth: number): number {
  if (!(rect.width > 0)) return 0;
  return ((clientX - rect.left) / rect.width) * viewWidth;
}
